import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import MonthFilter from '../components/MonthFilter';
import TableScrollContainer from '../components/TableScrollContainer';
import SearchableSelect from '../components/SearchableSelect';
import { buildMonthOptions, countDistinct, matchesSelectedMonths } from '../utils/monthFilters';
import { formatCurrency } from '../utils/currency';
import { formatServiceName } from '../utils/formatServiceName';
import './Dashboard.css';

function Dashboard() {
  const [servicos, setServicos] = useState([]);
  const [receitas, setReceitas] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedServico, setSelectedServico] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [servicosResponse, receitasResponse, despesasResponse] = await Promise.all([
          api.get('/servicos'),
          api.get('/lancamentos-receita'),
          api.get('/lancamentos-despesa'),
        ]);

        setServicos(servicosResponse.data);
        setReceitas(receitasResponse.data);
        setDespesas(despesasResponse.data);
      } catch (error) {
        const message = error.response?.data?.error || 'Não foi possível carregar o dashboard.';
        setErro(message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const monthOptions = useMemo(() => {
    return buildMonthOptions([
      ...receitas.map((item) => item.data_competencia || item.data_lancamento),
      ...despesas.map((item) => item.data_competencia || item.data_lancamento),
    ]);
  }, [receitas, despesas]);

  const receitasFiltradas = useMemo(() => {
    return receitas.filter((item) => {
      const matchesMonth = matchesSelectedMonths(item.data_competencia || item.data_lancamento, selectedMonths);
      const matchesServico = !selectedServico || String(item.servico_id) === String(selectedServico);
      return matchesMonth && matchesServico;
    });
  }, [receitas, selectedMonths, selectedServico]);

  const despesasFiltradas = useMemo(() => {
    return despesas.filter((item) => matchesSelectedMonths(item.data_competencia || item.data_lancamento, selectedMonths));
  }, [despesas, selectedMonths]);

  const resumo = useMemo(() => {
    const receitaMes = receitasFiltradas.reduce((sum, item) => sum + Number(item.valor || 0), 0);
    const comissaoMes = receitasFiltradas.reduce(
      (sum, item) => sum + Number(item.comissao != null ? item.comissao : Number(item.valor || 0) * 0.2),
      0
    );
    const despesaMes = despesasFiltradas.reduce((sum, item) => sum + Number(item.valor || 0), 0);

    return {
      total_clientes: countDistinct(receitasFiltradas.map((item) => item.cliente_id)),
      total_servicos: countDistinct(receitasFiltradas.map((item) => item.servico_id)),
      receita_mes: receitaMes,
      despesa_mes: despesaMes,
      lucro_liquido: receitaMes - despesaMes,
      comissao_mes: comissaoMes,
    };
  }, [despesasFiltradas, receitasFiltradas]);

  const ganhosPorCategoria = useMemo(() => {
    const categoriaByServicoId = new Map(
      servicos.map((servico) => [String(servico.id), servico.categoria || 'Sem categoria'])
    );

    const grouped = receitasFiltradas.reduce((acc, item) => {
      const categoria = categoriaByServicoId.get(String(item.servico_id)) || 'Sem categoria';
      const entry = acc.get(categoria) || { total: 0, quantidade: 0 };
      entry.total += Number(item.valor || 0);
      entry.quantidade += 1;
      acc.set(categoria, entry);
      return acc;
    }, new Map());

    return Array.from(grouped.entries())
      .map(([categoria, { total, quantidade }]) => ({ categoria, total, quantidade }))
      .sort((a, b) => b.total - a.total);
  }, [receitasFiltradas, servicos]);

  const ganhosPorFormaPagamento = useMemo(() => {
    const grouped = receitasFiltradas.reduce((acc, item) => {
      const forma = item.forma_pagamento || 'Não informado';
      const entry = acc.get(forma) || { total: 0, quantidade: 0 };
      entry.total += Number(item.valor || 0);
      entry.quantidade += 1;
      acc.set(forma, entry);
      return acc;
    }, new Map());

    return Array.from(grouped.entries())
      .map(([forma, { total, quantidade }]) => ({ forma, total, quantidade }))
      .sort((a, b) => b.total - a.total);
  }, [receitasFiltradas]);

  const receitaTotalPeriodo = useMemo(
    () => ganhosPorFormaPagamento.reduce((sum, item) => sum + item.total, 0),
    [ganhosPorFormaPagamento]
  );

  const quantidadeTotalPeriodo = receitasFiltradas.length;

  const formatPercent = (value) => {
    const percent = Number(value || 0);
    const rounded = Math.round(percent * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
  };

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      {erro && <div className="alert alert-error">{erro}</div>}
      <MonthFilter
        title="Filtrar meses"
        options={monthOptions}
        selectedMonths={selectedMonths}
        onChange={setSelectedMonths}
      />

      <div className="dashboard-toolbar card">
        <label className="dashboard-filter-field">
          <span>Serviço para análise</span>
          <SearchableSelect
            value={selectedServico}
            onChange={setSelectedServico}
            placeholder="Todos os serviços"
            searchPlaceholder="Digite para filtrar serviço"
            options={servicos.map((servico) => ({
              value: String(servico.id),
              label: formatServiceName(servico.nome),
              searchText: `${formatServiceName(servico.nome)} ${servico.descricao || ''}`,
            }))}
          />
        </label>
      </div>

      <div className="dashboard-grid">
        <div className="stat-card">
          <h3>Clientes no Período</h3>
          <p className="stat-value">{loading ? '...' : resumo.total_clientes}</p>
        </div>
        <div className="stat-card">
          <h3>Serviços no Período</h3>
          <p className="stat-value">{loading ? '...' : resumo.total_servicos}</p>
        </div>
        <div className="stat-card">
          <h3>Receita do Período</h3>
          <p className="stat-value">{loading ? '...' : formatCurrency(resumo.receita_mes)}</p>
        </div>
        <div className="stat-card">
          <h3>Despesa do Período</h3>
          <p className="stat-value">{loading ? '...' : formatCurrency(resumo.despesa_mes)}</p>
        </div>
        <div className="stat-card">
          <h3>Lucro Líquido</h3>
          <p className="stat-value">{loading ? '...' : formatCurrency(resumo.lucro_liquido)}</p>
        </div>
        <div className="stat-card">
          <h3>Comissão Total</h3>
          <p className="stat-value">{loading ? '...' : formatCurrency(resumo.comissao_mes)}</p>
        </div>
      </div>

      <div className="dashboard-breakdowns">
        <div className="card breakdown-card">
          <h2>Ganho por Categoria de Serviço</h2>
          {ganhosPorCategoria.length === 0 ? (
            <p className="empty-breakdown">Sem dados no período selecionado.</p>
          ) : (
            <TableScrollContainer rows={ganhosPorCategoria.length}>
              <table className="breakdown-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Valor</th>
                  <th>% Valor</th>
                  <th>Qtd</th>
                  <th>% Qtd</th>
                </tr>
              </thead>
              <tbody>
                {ganhosPorCategoria.map((item) => (
                  <tr key={item.categoria}>
                    <td>{item.categoria}</td>
                    <td>{formatCurrency(item.total)}</td>
                    <td>{formatPercent(receitaTotalPeriodo > 0 ? (item.total / receitaTotalPeriodo) * 100 : 0)}</td>
                    <td>{item.quantidade}</td>
                    <td>{formatPercent(quantidadeTotalPeriodo > 0 ? (item.quantidade / quantidadeTotalPeriodo) * 100 : 0)}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </TableScrollContainer>
          )}
        </div>

        <div className="card breakdown-card">
          <h2>Ganho por Forma de Pagamento</h2>
          {ganhosPorFormaPagamento.length === 0 ? (
            <p className="empty-breakdown">Sem dados no período selecionado.</p>
          ) : (
            <TableScrollContainer rows={ganhosPorFormaPagamento.length}>
              <table className="breakdown-table">
              <thead>
                <tr>
                  <th>Forma</th>
                  <th>Valor</th>
                  <th>% Valor</th>
                  <th>Qtd</th>
                  <th>% Qtd</th>
                </tr>
              </thead>
              <tbody>
                {ganhosPorFormaPagamento.map((item) => (
                  <tr key={item.forma}>
                    <td>{item.forma}</td>
                    <td>{formatCurrency(item.total)}</td>
                    <td>{formatPercent(receitaTotalPeriodo > 0 ? (item.total / receitaTotalPeriodo) * 100 : 0)}</td>
                    <td>{item.quantidade}</td>
                    <td>{formatPercent(quantidadeTotalPeriodo > 0 ? (item.quantidade / quantidadeTotalPeriodo) * 100 : 0)}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </TableScrollContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
